import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const YG_LOGO = "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/poi36fgm_younggunslighting%20logo.png";

export default function AdminLogin() {
  const { login, user, ready } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const redirectTo = loc.state?.from || "/admin";
  if (ready && user && user !== false) {
    setTimeout(() => nav(redirectTo, { replace: true }), 0);
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      nav(redirectTo, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid email or password.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05070A] text-white relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-[color:var(--primary)]/10 rounded-full blur-[140px]" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-[color:var(--secondary)]/15 rounded-full blur-[140px]" />
      </div>

      <div className="relative min-h-screen container-x flex items-center justify-center py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center mb-10">
            <img src={YG_LOGO} alt="Young Guns Lighting Solutions" className="h-24 md:h-28 w-auto object-contain drop-shadow-[0_0_40px_rgba(0,229,255,0.4)]" />
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="border border-white/10 bg-[#0C1017] p-8 md:p-10"
          >
            <div className="flex items-center gap-2 mb-6">
              <Lock size={14} className="text-[color:var(--primary)]" />
              <div className="font-mono-accent text-[10px] tracking-[0.35em] uppercase text-[color:var(--primary)]">
                Admin Access
              </div>
            </div>
            <h1 className="font-display uppercase text-white text-3xl md:text-4xl tracking-tight leading-tight mb-8">
              Sign in
            </h1>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
              <div className="space-y-2">
                <Label className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/50" htmlFor="admin-email">Email</Label>
                <Input
                  id="admin-email"
                  data-testid="admin-login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@younggunslighting.com"
                  className="bg-[#05070A] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[color:var(--primary)] focus-visible:ring-offset-0 rounded-none h-11"
                  autoComplete="email"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/50" htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  data-testid="admin-login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-[#05070A] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[color:var(--primary)] focus-visible:ring-offset-0 rounded-none h-11"
                  autoComplete="current-password"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="admin-login-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[color:var(--primary)] text-black font-mono-accent text-[11px] tracking-[0.25em] uppercase hover:bg-white transition-colors btn-sharp disabled:opacity-60"
              >
                {busy ? <><Loader2 className="animate-spin" size={14} /> Signing in</> : <>Sign in <ArrowRight size={14} /></>}
              </button>
            </form>
          </motion.div>

          <Link to="/" className="mt-8 block text-center font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40 hover:text-[color:var(--primary)] transition-colors">
            ← Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}
